// Top-level component. BrowserRouter wraps the AppShell, which wraps the
// per-route page. v1 has one real page (TodayPage); the rest are stubs
// so the sidebar nav doesn't 404.
//
// Auth gate: queries /api/me at the root. If the request errors with a
// 401 (no Supabase session AND no dev bypass), renders the Login page
// instead of the routed shell. Once /auth/verify mints a session, the
// next /api/me succeeds and the gate falls through.

import { useQuery } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell.js';
import { AdminAppShell } from './components/admin/AdminAppShell.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Login } from './components/Login.js';
import { ReconnectingScreen } from './components/ReconnectingScreen.js';
import { authGateState } from './lib/auth-gate.js';
import { TodayPage } from './pages/TodayPage.js';
import { CustomersPage } from './pages/CustomersPage.js';
import { InventoryPage } from './pages/InventoryPage.js';
import { JobsPage } from './pages/JobsPage.js';
import { JobDetailPage } from './pages/JobDetailPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { FinancialsPage } from './pages/FinancialsPage.js';
import { HoursPage } from './pages/HoursPage.js';
import { CalendarPage } from './pages/CalendarPage.js';
import { UsersPage } from './pages/admin/UsersPage.js';
import { AnalyticsPage } from './pages/admin/AnalyticsPage.js';
import { StoragePage } from './pages/admin/StoragePage.js';
import { getMe, is401Error, type MeResponse } from './api/client.js';
import { AnalyticsBeacon } from './hooks/useAnalyticsPageview.js';

// Reset the error boundary on route change so a stuck error doesn't
// trap the user on a different page.
function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

export function App() {
  const { data: me, isPending, error, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
    retry: (failureCount, err) => {
      // Don't retry on 401 — that's the auth boundary, not a transient
      // failure. Non-401 (5xx / network) gets a couple of backed-off
      // retries so a flaky-worker blip during the post-login boot doesn't
      // immediately fall through to a (false) logged-out state.
      if (is401Error(err)) return false;
      return failureCount < 2;
    },
    retryDelay: (i) => Math.min(500 * 2 ** i, 2000),
  });

  // Translate the query state into a screen. 401 → Login (real boundary);
  // any other settled error → Reconnecting (transient, retryable) instead
  // of a false logout. See lib/auth-gate.ts.
  const gate = authGateState({ isPending, error, hasMe: !!me });

  if (gate === 'loading') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-sm font-medium text-muted-foreground">
          Loading…
        </div>
      </main>
    );
  }

  if (gate === 'reconnecting') {
    return <ReconnectingScreen onRetry={() => void refetch()} />;
  }

  // gate === 'login', plus a defensive !me guard that also narrows the type.
  if (gate === 'login' || !me) {
    return <Login />;
  }

  return (
    <BrowserRouter>
      <AnalyticsBeacon />
      <RouteErrorBoundary>
        <Routes>
          {/* Admin routes — gated on role='admin'; non-admins bounce to "/" */}
          <Route
            path="/admin/users"
            element={
              me.role === 'admin' ? (
                <AdminAppShell me={me}>
                  <UsersPage me={me} />
                </AdminAppShell>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/analytics"
            element={
              me.role === 'admin' ? (
                <AdminAppShell me={me}>
                  <AnalyticsPage />
                </AdminAppShell>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/storage"
            element={
              me.role === 'admin' ? (
                <AdminAppShell me={me}>
                  <StoragePage />
                </AdminAppShell>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin"
            element={<Navigate to="/admin/users" replace />}
          />
          {/* Main app routes */}
          <Route
            path="*"
            element={
              <AppShell>
                <Routes>
                  <Route path="/" element={<TodayPage />} />
                  <Route path="/customers" element={<CustomersPage />} />
                  <Route path="/jobs" element={<JobsPage />} />
                  <Route path="/jobs/:id" element={<JobDetailPage />} />
                  <Route path="/inventory" element={<InventoryPage />} />
                  <Route path="/calendar" element={<CalendarPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  {/* Financials — office-wide (owner/admin/back_office); a non-office
                      role bounces to home. Mirrors the Hours gate below. */}
                  <Route
                    path="/financials"
                    element={
                      me.role === 'owner' || me.role === 'admin' || me.role === 'back_office' ? (
                        <FinancialsPage />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                  {/* Hours — office-wide (owner/admin/back_office). Gate mirrors
                      Financials so a non-office role bounces to home. */}
                  <Route
                    path="/hours"
                    element={
                      me.role === 'owner' || me.role === 'admin' || me.role === 'back_office' ? (
                        <HoursPage />
                      ) : (
                        <Navigate to="/" replace />
                      )
                    }
                  />
                </Routes>
              </AppShell>
            }
          />
        </Routes>
      </RouteErrorBoundary>
    </BrowserRouter>
  );
}

// Silence unused-import lint until the type is referenced; UsersPage
// uses MeResponse through its own import. This keeps `MeResponse`
// re-exported from this file as a convenience for future admin pages.
export type { MeResponse };
